import type { ICellRendererParams, RowClassParams } from 'ag-grid-community';
import type { Dictionary } from 'lodash';
import { CostRate, CostRateSourceType } from 'api/cost-rates';
import { UserStatus } from 'api/models';
import type { FilterableField } from 'components/organisms/tasks/fields';
import type { Member, User } from 'data/members/api';
import { getFieldMapReducer } from 'routes/projects/list-v2/transformer';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { UserFieldsName } from 'utils/user-fields';
import { TIMESHEET_ADMIN_ID } from '../../partner/useAdminOption';
import { TeamMembersTabsEnum } from '../types';

type PartialCostRate = Pick<CostRate, 'currency' | 'hourlyCostRate'>;

interface Data {
  data: Member & { costRate: PartialCostRate };
}
type WithData<AgGridType> = Pick<
  AgGridType,
  Exclude<keyof AgGridType, 'data'>
> &
  Data;

export type MemberCellRendererParams = WithData<ICellRendererParams>;

export const setDeactivedRowStyle = (params: RowClassParams) =>
  params.data?.user?.userStatus === UserStatus.Inactive
    ? { opacity: '0.4' }
    : undefined;

export const memberNameSorter = (prevUser: User, nextUser: User): number =>
  sortByText(fullName(prevUser), fullName(nextUser));

export const permissionSorter = (
  permissionSetA: Member['permissionSet'],
  permissionSetB: Member['permissionSet'],
): number => {
  const permissionNameKeyA = permissionSetA?.permissionSetName;
  const permissionNameKeyB = permissionSetB?.permissionSetName;

  return sortByText(permissionNameKeyA, permissionNameKeyB);
};

export const tabToUserStatusMap = {
  [TeamMembersTabsEnum.Active]: UserStatus.Active,
  [TeamMembersTabsEnum.Deactivated]: UserStatus.Inactive,
  [TeamMembersTabsEnum.Invited]: UserStatus.Invited,
};

const filter = {
  [TeamMembersTabsEnum.Active]: (_members: Member[]) =>
    _members.filter(({ user }) => user.userStatus === UserStatus.Active),
  [TeamMembersTabsEnum.Invited]: (_members: Member[]) =>
    _members.filter(({ user }) => user.userStatus === UserStatus.Invited),
  [TeamMembersTabsEnum.Deactivated]: (_members: Member[]) =>
    _members.filter(({ user }) => user.userStatus === UserStatus.Inactive),
};

export const transform = (
  fieldLookup: Dictionary<FilterableField>,
  data: Member[],
  costRates: CostRate[] = [],
): Member[] => {
  const toFieldMap = getFieldMapReducer(fieldLookup);

  const userLevelCostRateMap: Dictionary<PartialCostRate> = (costRates ?? [])
    .filter((cost) => cost.sourceType === CostRateSourceType.User)
    .reduce(
      (map, currEntry) => ({
        ...map,
        [`${currEntry.sourceId}_${CostRateSourceType.User}`]: {
          hourlyCostRate: currEntry.hourlyCostRate,
          currency: currEntry.currency,
        },
      }),
      {},
    );

  const roleLevelCostRateMap: Dictionary<PartialCostRate> = (costRates ?? [])
    .filter((cost) => cost.sourceType === CostRateSourceType.Role)
    .reduce(
      (map, currEntry) => ({
        ...map,
        [`${currEntry.sourceId}_${CostRateSourceType.Role}`]: {
          hourlyCostRate: currEntry.hourlyCostRate,
          currency: currEntry.currency,
        },
      }),
      {},
    );

  const newData: Member[] = data?.reduce(
    (list, { permissionSet, user, ...rest }) => {
      const fieldsMap = (user?.fields ?? []).reduce(toFieldMap, {});

      return [
        ...list,
        {
          ...rest,
          ...fieldsMap,
          user,
          permissionSet,
          [UserFieldsName.UserId]: user?.userId,
          [UserFieldsName.EmailId]: user?.emailId,
          [UserFieldsName.Role]: user?.role?.roleId,
          [UserFieldsName.PermissionSetId]: permissionSet?.permissionSetId,
          [UserFieldsName.TimeSheetApprover]:
            user?.timeSheetApprover ?? TIMESHEET_ADMIN_ID,
          [UserFieldsName.WeeklyCapacity]: user?.capacityMinutes,
          [UserFieldsName.HolidayCalendarId]: user?.holidayCalendarId,
          [UserFieldsName.CostRate]:
            userLevelCostRateMap[
              `${user?.userId}_${CostRateSourceType.User}`
            ] ||
            roleLevelCostRateMap[
              `${user?.role?.roleId}_${CostRateSourceType.Role}`
            ],
        },
      ];
    },
    [] as Member[],
  );

  return newData;
};

interface RowData {
  data: Member[];
  costRates: CostRate[] | [];
  fieldLookupById: Dictionary<FilterableField>;
  tab: TeamMembersTabsEnum;
}
const transformGroupUsersData = (
  fieldLookup: Dictionary<FilterableField>,
  data: Member[],
  costRates: CostRate[] = [],
) => {
  const toFieldMap = getFieldMapReducer(fieldLookup);

  const userLevelCostRateMap: Dictionary<PartialCostRate> = (costRates ?? [])
    .filter((cost) => cost.sourceType === CostRateSourceType.User)
    .reduce(
      (map, currEntry) => ({
        ...map,
        [`${currEntry.sourceId}_${CostRateSourceType.User}`]: {
          hourlyCostRate: currEntry.hourlyCostRate,
          currency: currEntry.currency,
        },
      }),
      {},
    );

  const roleLevelCostRateMap: Dictionary<PartialCostRate> = (costRates ?? [])
    .filter((cost) => cost.sourceType === CostRateSourceType.Role)
    .reduce(
      (map, currEntry) => ({
        ...map,
        [`${currEntry.sourceId}_${CostRateSourceType.Role}`]: {
          hourlyCostRate: currEntry.hourlyCostRate,
          currency: currEntry.currency,
        },
      }),
      {},
    );

  const newData: Member[] = data?.reduce(
    (list, { permissionSet, user, ...rest }) => {
      const fieldsMap = (user?.fields ?? []).reduce(toFieldMap, {});

      list.push({
        ...rest,
        ...fieldsMap,
        user,
        permissionSet,
        [UserFieldsName.UserId]: user.userId,
        [UserFieldsName.EmailId]: user.emailId,
        [UserFieldsName.Role]: user.role?.roleId,
        [UserFieldsName.PermissionSetId]: permissionSet?.permissionSetId,
        [UserFieldsName.TimeSheetApprover]: user.timeSheetApprover ?? -1,
        [UserFieldsName.WeeklyCapacity]: user.capacityMinutes,
        [UserFieldsName.HolidayCalendarId]: user.holidayCalendarId,
        [UserFieldsName.CostRate]:
          userLevelCostRateMap[`${user.userId}_${CostRateSourceType.User}`] ||
          roleLevelCostRateMap[
            `${user.role?.roleId}_${CostRateSourceType.Role}`
          ],
      });

      return list;
    },
    [] as Member[],
  );

  return newData;
};

export const computeRowDataForTeamMembers = ({
  data,
  costRates,
  fieldLookupById,
  tab,
}: RowData) => {
  const rowData = transform(fieldLookupById, data, costRates);

  return filter[tab](rowData);
};

export const computeRowDataForGroupMembers = ({
  data,
  costRates,
  fieldLookupById,
}) => {
  const rowData = transformGroupUsersData(fieldLookupById, data, costRates);

  return rowData;
};
